/**
 * Причины дефицита ёмкости. Состояние одно — слотов меньше заказанного, — а
 * причин четыре, и действия у оператора РАЗНЫЕ: сток провайдера, отказ
 * провайдера, нечитаемый список его серверов и просевшая телефония чинятся в
 * разных местах. Поэтому причина обязана быть на экране словами, а не кодом.
 *
 * Код приходит тремя путями с одним словарём: `reason` ответа ручного прогрева
 * (`POST /api/fleet/scale-up`), `details.reason` сигнала `deficit` на доске и
 * `reason` сигнала в снимке прогона автоскейлера.
 */
export interface DeficitReasonMeta {
  /** Что произошло — заголовок, читаемый без словаря. */
  title: string
  /** Почему так — механика, из которой следует действие. */
  detail: string
  /** Что делать оператору. Ровно этим три причины и различаются. */
  action: string
}

const reasons: Record<string, DeficitReasonMeta> = {
  'no-stock': {
    title: 'Карт у провайдера нет',
    detail:
      'Ни одного свободного сервера нужного класса в стоке не нашлось — заказ не отправлялся вовсе. Ядро повторяет попытку с нарастающей паузой: от 2 минут до получаса.',
    action: 'Ждать сток либо завести второй аккаунт провайдера и назначить ему эталоны клона.',
  },
  'provider-failed': {
    title: 'Провайдер отказал в заказе',
    detail:
      'Карты в стоке были, но создание сервера не прошло, и юнит ушёл в error. Прогон продолжился по остальным слотам — половина ячейки лучше, чем ни одной.',
    action: 'Кабинет провайдера: баланс, лимиты аккаунта, живость API-ключа.',
  },
  'provider-scan-failed': {
    title: 'Скан провайдера не удался — заказ отложен',
    detail:
      'Список серверов у провайдера не прочитался, а имя сервера служит ключом идемпотентности заказа: вслепую тик заказывать не стал, чтобы обрыв ответа не превратился во второй оплаченный сервер. Ни одной карты не заказано; следующий тик повторит скан.',
    action:
      'Ждать следующего тика. Повторяется — кабинет провайдера: живость list-API и API-ключа аккаунта.',
  },
  'lines-shrunk': {
    title: 'Линии ушли из-под живой заявки',
    detail:
      'Железо стоит и оплачивается, а звонить не на чем: учётку пула выключили, урезали каналы или пометили degraded. Честный слот — min(слоты железа, арендованные линии), поэтому ёмкость просела без единой поломки во флоте.',
    action: 'Телефония: учётки пула и их каналы — сначала линии, железо уже есть.',
  },
}

/**
 * Причина по коду. Пустой код и незнакомый код — разные вещи: первое значит
 * «ядро причину не назвало», второе «словарь отстал от бэка», и оба честнее
 * молчания.
 */
export function deficitReason(code: string): DeficitReasonMeta {
  const known = reasons[code]
  if (known) return known
  if (code === '') {
    return {
      title: 'Причину ядро не назвало',
      detail:
        'Прогрев прошёл без кода причины. Так бывает, когда слоты покрыты и дефицита нет вовсе.',
      action: 'Сверь готовые слоты заявки: если их меньше заказанных — повтори прогрев.',
    }
  }
  return {
    title: `Причина «${code}»`,
    detail: 'Код причины админке незнаком — словарь отстал от ядра.',
    action: 'Смотри структурный лог backend’а по этой заявке.',
  }
}

export const DEFICIT_REASONS = Object.keys(reasons)
